/**
 * A rail row, drawn as finixt-ui's NavItem and rendered as a real link.
 *
 * finixt-ui's row is a button with an onClick, which a router can follow but
 * a browser cannot: no href, no middle click, no "open in new tab", nothing
 * for assistive technology to call the current page. This one is a
 * react-router Link with aria-current, so the rail reads as navigation.
 *
 * The quiet mono count is a size; a toned badge is a state that needs
 * doing, which is why it may be amber or red where finixt-ui's is always
 * accent. A row may carry two, drawn in order. Collapsed, the label and
 * count go and the first badge becomes a dot.
 */
import { useContext, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';

import { Icon, ShellContext } from './finixt.js';

export type NavBadgeTone = 'accent' | 'warn' | 'danger';

export interface NavBadge {
  value: number | string;
  tone: NavBadgeTone;
}

export interface NavLinkProps {
  to: string;
  /** Material Symbols ligature. */
  icon: string;
  label: string;
  active?: boolean | undefined;
  /** Quiet mono record count. */
  count?: number | string | undefined;
  /** Outrank the count when both are set. */
  badges?: readonly NavBadge[] | undefined;
  /** Glyph only. Read from ShellContext when omitted. */
  collapsed?: boolean | undefined;
  /** For a row that must also do something, such as close the drawer. */
  onClick?: (() => void) | undefined;
}

const BADGE_FILL: Record<NavBadgeTone, string> = {
  accent: 'var(--accent)',
  warn: 'var(--warn)',
  danger: 'var(--danger)',
};

/** The rail row. */
export function NavLink({
  to,
  icon,
  label,
  active = false,
  count,
  badges,
  collapsed: collapsedProp,
  onClick,
}: NavLinkProps) {
  const shell = useContext(ShellContext);
  const collapsed = collapsedProp ?? Boolean(shell.railCollapsed);
  const [hover, setHover] = useState(false);
  const style: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    width: '100%',
    boxSizing: 'border-box',
    position: 'relative',
    padding: collapsed ? '9px 0' : '7px 9px',
    justifyContent: collapsed ? 'center' : undefined,
    borderRadius: 'var(--radius-input)',
    background: active ? 'var(--surface-active)' : hover ? 'var(--grey-100)' : 'transparent',
    color: active ? 'var(--grey-950)' : 'var(--grey-850)',
    fontFamily: 'var(--font-sans)',
    fontSize: 'var(--text-body-md)',
    fontWeight: active ? 'var(--weight-semibold)' : 'var(--weight-medium)',
    letterSpacing: 'var(--tracking-base)',
    textDecoration: 'none',
    textAlign: 'left',
  };
  return (
    <Link
      to={to}
      aria-current={active ? 'page' : undefined}
      title={collapsed ? label : undefined}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={style}
    >
      <Icon name={icon} size={19} />
      {collapsed ? (
        badges?.[0] ? (
          <span
            style={{
              position: 'absolute',
              top: 6,
              right: 9,
              width: 6,
              height: 6,
              borderRadius: 'var(--radius-round)',
              background: BADGE_FILL[badges[0].tone],
            }}
          />
        ) : null
      ) : (
        <>
          <span
            style={{
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </span>
          {badges && badges.length > 0 ? (
            badges.map((badge) => (
              <span
                key={badge.tone}
                className="mono"
                style={{
                  fontSize: 'var(--text-label)',
                  color: 'var(--on-fill)',
                  background: BADGE_FILL[badge.tone],
                  borderRadius: 'var(--radius-badge)',
                  padding: '1px 5px',
                }}
              >
                {badge.value}
              </span>
            ))
          ) : count !== undefined ? (
            <span
              className="mono"
              style={{ fontSize: 'var(--text-label-lg)', color: 'var(--grey-600)' }}
            >
              {count}
            </span>
          ) : null}
        </>
      )}
    </Link>
  );
}
